import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  beginDuctedSlotPreferencesCopy,
  endDuctedSlotPreferencesCopy,
  setSystemRecommendationSelections,
  setWantCompositionPicks,
  StructureState,
  SystemRecommendationSelectionsEntry
} from 'features/structure/structure-slice';
import axios from 'utils/api';
import {
  getDuctedNewSystemAnchorSlot,
  getDuctedNewSystemSlots
} from 'utils/ducted-new-systems';
import {
  DuctedSlotPreferenceSeed,
  PlanDuctedSlotPreferenceSeedsParams
} from 'utils/ducted-slot-preferences-types';
import { compositionHasEquipmentType } from 'utils/composition-picks';
import { isRecommendationStripCompleted } from 'utils/existing-equipment';
import { RecommendationSelections } from 'utils/recommendations-strip-logic';
import { buildSizeRecordSelectionsPayload } from 'utils/size-record-persist';

export function buildDuctedSlotPreferencesPayload(
  sourceEntry: SystemRecommendationSelectionsEntry | null | undefined
): RecommendationSelections {
  const payload: RecommendationSelections = {
    wantIds: sourceEntry?.wantIds ?? [],
    haveId: sourceEntry?.haveId ?? null,
    refrigerant: sourceEntry?.refrigerant ?? null,
    age: sourceEntry?.age ?? null
  };
  if (sourceEntry?.useRecommendations !== undefined) {
    payload.useRecommendations = sourceEntry.useRecommendations;
  }
  if (sourceEntry?.fingerprint !== undefined) {
    payload.fingerprint = sourceEntry.fingerprint;
  }
  if (sourceEntry?.virtualCodes !== undefined) {
    payload.virtualCodes = sourceEntry.virtualCodes;
  }
  if (sourceEntry?.virtualFuel !== undefined) {
    payload.virtualFuel = sourceEntry.virtualFuel;
  }
  return payload;
}

export function planDuctedSlotPreferenceSeeds({
  existingSystems,
  recommendationSelectionsBySystem,
  wantCompositionPicksBySystem,
  isAwaitingFirstSave
}: PlanDuctedSlotPreferenceSeedsParams): DuctedSlotPreferenceSeed[] {
  const isStripCompleted = (systemUniqueId: string) =>
    isRecommendationStripCompleted(
      recommendationSelectionsBySystem[systemUniqueId],
      wantCompositionPicksBySystem[systemUniqueId]
    );
  const anchor = getDuctedNewSystemAnchorSlot(existingSystems);
  if (!anchor || !isStripCompleted(anchor.uniqueId)) return [];

  return getDuctedNewSystemSlots(existingSystems)
    .filter(
      (slot) =>
        slot.uniqueId !== anchor.uniqueId &&
        !isStripCompleted(slot.uniqueId) &&
        !isAwaitingFirstSave(slot.uniqueId)
    )
    .map((slot) => ({
      sourceSystemUniqueId: anchor.uniqueId,
      targetSystemUniqueId: slot.uniqueId
    }));
}

function hasDuctedSlot(
  { existingSystems }: StructureState,
  systemUniqueId: string
): boolean {
  return existingSystems.some((system) => system.uniqueId === systemUniqueId);
}

async function patchDuctedSlotPreferences(
  systemUniqueId: string,
  selections: RecommendationSelections
): Promise<boolean> {
  try {
    await axios.patch(
      `existing-equipment/${systemUniqueId}/recommendation-selections`,
      selections
    );
    return true;
  } catch (error) {
    console.error(
      `Error saving equipment preferences for system ${systemUniqueId}:`,
      error
    );
    return false;
  }
}

export const copyDuctedSlotPreferences = createAsyncThunk<
  boolean,
  DuctedSlotPreferenceSeed
>(
  'structure/copyDuctedSlotPreferences',
  async (
    { sourceSystemUniqueId, targetSystemUniqueId },
    { getState, dispatch }
  ) => {
    const { recommendationSelectionsBySystem, wantCompositionPicksBySystem } = (
      getState() as RootState
    ).structure;
    const selections = buildDuctedSlotPreferencesPayload(
      recommendationSelectionsBySystem[sourceSystemUniqueId]
    );
    const picks = wantCompositionPicksBySystem[sourceSystemUniqueId];

    dispatch(beginDuctedSlotPreferencesCopy({ targetSystemUniqueId }));
    try {
      const patched = await patchDuctedSlotPreferences(
        targetSystemUniqueId,
        selections
      );
      if (!patched) return false;

      const { structure } = getState() as RootState;
      if (!hasDuctedSlot(structure, targetSystemUniqueId)) return false;

      const ownSelections =
        structure.recommendationSelectionsBySystem[targetSystemUniqueId];
      if (isRecommendationStripCompleted(ownSelections)) {
        await patchDuctedSlotPreferences(
          targetSystemUniqueId,
          buildSizeRecordSelectionsPayload(ownSelections, {})
        );
        return false;
      }

      dispatch(
        setSystemRecommendationSelections({
          systemUniqueId: targetSystemUniqueId,
          selections
        })
      );
      const ownPicks =
        structure.wantCompositionPicksBySystem[targetSystemUniqueId];
      if (picks && !compositionHasEquipmentType(ownPicks)) {
        dispatch(
          setWantCompositionPicks({
            systemUniqueId: targetSystemUniqueId,
            picks
          })
        );
      }
      return true;
    } finally {
      dispatch(endDuctedSlotPreferencesCopy({ targetSystemUniqueId }));
    }
  },
  {
    condition: ({ targetSystemUniqueId }, { getState }) =>
      !(getState() as RootState).structure.ductedSlotCopiesInFlight.includes(
        targetSystemUniqueId
      )
  }
);
